import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { DIRECTION, KEYCODE_ENTER, KEYCODE_ESC } from '../../constants';
import { ResultCounterContainer, SearchButton, SearchControlsContainer } from '.';

const INPUT_ID = 'search-input';

export default function SearchInput({
  sendQuery,
  goToNext,
  goToPrev,
  cancelSearch,
  query,
}) {
  const [isTyping, setIsTyping] = useState(true);
  const setTypingTrue = useCallback(() => { setIsTyping(true); }, [setIsTyping]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  const handleSubmit = useCallback((direction, navigateAction) => {
    query !== inputRef.current.value
      ? sendQuery(inputRef.current.value, direction)
      : navigateAction();

    setIsTyping(false);
  }, [query, inputRef, sendQuery, setIsTyping]);

  const containerKeyDown = useCallback((event) => {
    if (event.keyCode === KEYCODE_ESC) {
      cancelSearch();
      event.stopPropagation();
    }
  }, [cancelSearch]);

  const inputKeyDown = useCallback((event) => {
    if (event.keyCode === KEYCODE_ENTER) {
      const reverseSearch = event.shiftKey;
      const direction = reverseSearch ? DIRECTION.DESC : DIRECTION.ASC;
      const navigateAction = reverseSearch ? goToPrev : goToNext;
      handleSubmit(direction, navigateAction);
      event.preventDefault();
    }
  }, [handleSubmit, goToPrev, goToNext]);

  return (
    // this div element has child input and button elements which allow keyboard interactions
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="search-input-container" onKeyDown={containerKeyDown}>
      <span className={classnames('underline', { active: isTyping })}>
        <input
          ref={inputRef}
          id={INPUT_ID}
          className="search-input"
          onKeyDown={inputKeyDown}
          onFocus={setTypingTrue}
          onChange={setTypingTrue}
          placeholder="Search in this article"
          title="Search in this article"
          type="text"
        />
        {!isTyping
          ? <ResultCounterContainer />
          : <SearchButton onClick={() => handleSubmit(DIRECTION.ASC, goToNext)} />
        }
      </span>
      <SearchControlsContainer isTyping={isTyping} />
    </div>
  );
}

SearchInput.propTypes = {
  sendQuery: PropTypes.func.isRequired,
  goToNext: PropTypes.func.isRequired,
  goToPrev: PropTypes.func.isRequired,
  cancelSearch: PropTypes.func.isRequired,
  query: PropTypes.string,
};

SearchInput.defaultProps = {
  query: '',
};

/**
 * This somewhat breaks encapsulation and represents manual intervention rather than relying on
 * react state and lifecycle hooks, however it was decided on balance this was a simpler and more
 * pragmatic solution than pushing the focus state through the store.
 */
export function focusInput() {
  const searchInput = document.getElementById(INPUT_ID);
  if (searchInput) {
    searchInput.focus();
    searchInput.select();
  }
}
